import { useNavigate } from '@tanstack/react-router'
import { CheckCircleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import FatalError from '@/shared/components/FatalError'
import { Operators } from '@/shared/components/query-builder'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Layout } from '@/shared/components/ui/layout'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { getFiltersQueryString } from '@/shared/tools/location'

import {
	usePricingAlertsAnnotationsQuery,
	usePricingAlertsQuery,
} from '../api/queries'
import { getPricingAlertsTree } from '../lib/helpers'
import { PricingAlerts } from './PricingAlerts'

export const PricingAlertsGlobalWidget = ({
	currency,
}: {
	currency?: string
}) => {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const pricingAlertsAnnotations = usePricingAlertsAnnotationsQuery()
	const pricingAlerts = usePricingAlertsQuery({ currency })
	const isLoading =
		pricingAlertsAnnotations.isLoading || pricingAlerts.isLoading
	const isError = pricingAlertsAnnotations.isError || pricingAlerts.isError
	if (isLoading) {
		return <AlertsListSkeleton />
	}
	if (isError) {
		return <FatalError />
	}
	const alertsTree = getPricingAlertsTree({
		pricingAlerts: pricingAlerts.data,
		annotations: pricingAlertsAnnotations.data,
	})
	const handleNavigate = (ids: string[]) => {
		navigate({
			to: `${location.pathname}${getFiltersQueryString([
				{ name: 'alerts', operation: Operators.ANY_OF, value: ids },
			])}`,
		})
	}

	return (
		<div className='w-full'>
			{!alertsTree?.length ? (
				<Empty data-testid='no-data'>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<CheckCircleIcon />
						</EmptyMedia>
						<EmptyTitle>
							{t('general_everything_is_ok', {
								defaultValue: 'Everything is ok',
							})}
						</EmptyTitle>
						<EmptyDescription>
							{t('general_everything_is_ok_description', {
								defaultValue: 'Everything is ok. You have no pricing alerts.',
							})}
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			) : (
				<PricingAlerts
					alertsOf='assortment'
					alertsTree={alertsTree}
					onNavigate={handleNavigate}
				/>
			)}
		</div>
	)
}
const AlertsListSkeleton = () => (
	<Layout className='h-full overflow-y-hidden'>
		<div className='flex w-full flex-col justify-center gap-2 p-4'>
			<Skeleton className='h-12 w-full' />
			<Skeleton className='h-12 w-full opacity-70' />
			<Skeleton className='h-12 w-full opacity-50' />
			<Skeleton className='h-12 w-full opacity-30' />
			<Skeleton className='h-12 w-full opacity-10' />
		</div>
	</Layout>
)
